import { lazy, Suspense } from 'react'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import NumberRating from 'components/Common/NumberRating'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HotelDetailAmenities from './HotelDetailAmenities'
import HotelDetailInclusions from './HotelDetailInclusions'
import HotelDetailPricing from './HotelDetailPricing'
import HotelDetailRoomOptions from './HotelDetailRoomOptions'
import HotelDetailBookedRoom from './HotelDetailBookedRoom'
import { ContentWrapper } from './HotelListingDrawer'
import { OfferDetailImageGallery, OfferDetailDescription, OfferDetailReviews, OfferDetailMap } from '../OfferDetail'
import type { OfferDetailReview } from '../OfferDetail'
import type { DrawerHotel } from './types'
import type { DrawerHotelBookedRoom } from 'tripPlanner/utils/types'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_BED_BANK, OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'

const DrawerFlashHotelRoomOptions = lazy(() => import('./DrawerFlashHotelRoomOptions'))
const DrawerBedbankRoomOptions = lazy(() => import('./DrawerBedbankRoomOptions'))

interface Props {
  hotel: DrawerHotel
  reviews?: Array<OfferDetailReview>
  reviewsLoading?: boolean
  isBooked?: boolean
  orderId?: string
  savedBookedRooms?: Array<DrawerHotelBookedRoom>
  itineraryData?: TripItineraryData
  contextStop?: TripStop
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onAfterAction?: () => void
  checkIn?: string
  checkOut?: string
  initialOccupancies?: Array<App.Occupants>
  selectedPackageId?: string
  selectedRoomTypeId?: string
  scrollToRoomOptions?: boolean
  checkoutSource?: App.CheckoutSource
}

function HotelDetailView({ hotel, reviews, reviewsLoading, isBooked, orderId, savedBookedRooms, itineraryData, contextStop, onSendMessage, onAfterAction, checkIn, checkOut, initialOccupancies, selectedPackageId, selectedRoomTypeId, scrollToRoomOptions, checkoutSource }: Props) {
  const {
    name,
    location,
    description,
    images,
    rating,
    reviewsTotal,
    inclusions,
    amenities,
    price,
    luxPlusPrice,
    value,
    discountPercent,
    memberDiscountPercent,
    offerId,
    deeplinkUrl,
    geoLocation,
    saleUnit,
  } = hotel

  const hasDescription = !!description
  const hasAmenities = !!amenities && amenities.length > 0
  const hasInclusions = !!inclusions && inclusions.length > 0
  const hasReviews = !!reviews && reviews.length > 0
  const hasGeoLocation = !!geoLocation
  const roomOptions = !isBooked ? hotel.roomOptions : undefined
  const hasRoomOptions = !!roomOptions && roomOptions.length > 0
  const resolvedBookedRooms = savedBookedRooms ?? hotel.bookedRooms
  const hasBookedRooms = isBooked && !!resolvedBookedRooms && resolvedBookedRooms.length > 0
  const isFlashHotel = !isBooked && (hotel.offerType === OFFER_TYPE_HOTEL || hotel.offerType === OFFER_TYPE_LAST_MINUTE || hotel.offerType === OFFER_TYPE_ALWAYS_ON)
  const isBedbankHotel = !isBooked && hotel.offerType === OFFER_TYPE_BED_BANK

  return (
    <ContentWrapper>
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={12}>
          <Group direction="vertical">
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <Heading variant="heading2">{name}</Heading>
              {isBooked && <BookedLabel />}
            </Group>
            <BodyText variant="medium" colour="light-foreground-alt">{location}</BodyText>
          </Group>
          {rating !== undefined && rating > 0 && (
            <NumberRating
              rating={rating}
              total={reviewsTotal}
              variant="small"
              squareContainer
            />
          )}
        </Group>
        <OfferDetailImageGallery images={images} />

        {hasDescription && (
          <OfferDetailDescription description={description} />
        )}

        <Divider kind="secondary" />

        <HotelDetailPricing
          price={price}
          luxPlusPrice={luxPlusPrice}
          value={value}
          discountPercent={discountPercent}
          memberDiscountPercent={memberDiscountPercent}
          offerId={offerId}
          deeplinkUrl={deeplinkUrl}
          saleUnit={saleUnit}
          isBooked={isBooked}
          orderId={orderId}
        />

        {hasAmenities && (
          <>
            <Divider kind="secondary" />
            <HotelDetailAmenities amenities={amenities} />
          </>
        )}

        {hasInclusions && (
          <>
            <Divider kind="secondary" />
            <HotelDetailInclusions inclusions={inclusions} />
          </>
        )}

        {hasBookedRooms && (
          <>
            <Divider kind="secondary" />
            <HotelDetailBookedRoom bookedRooms={resolvedBookedRooms} />
          </>
        )}

        {isFlashHotel && (
          <>
            <Divider kind="secondary" />
            <Suspense fallback={<LoadingIndicator inline visible />}>
              <DrawerFlashHotelRoomOptions
                offerId={offerId}
                checkIn={checkIn}
                checkOut={checkOut}
                hotel={hotel}
                itineraryData={itineraryData}
                contextStop={contextStop}
                onSendMessage={onSendMessage}
                onAfterAction={onAfterAction}
                initialOccupancies={initialOccupancies}
                selectedPackageId={selectedPackageId}
                selectedRoomTypeId={selectedRoomTypeId}
                scrollToRoomOptions={scrollToRoomOptions}
                checkoutSource={checkoutSource}
              />
            </Suspense>
          </>
        )}

        {isBedbankHotel && (
          <>
            <Divider kind="secondary" />
            <Suspense fallback={<LoadingIndicator inline visible />}>
              <DrawerBedbankRoomOptions
                offerId={offerId}
                checkIn={checkIn}
                checkOut={checkOut}
                initialOccupancies={initialOccupancies}
                hotel={hotel}
                itineraryData={itineraryData}
                contextStop={contextStop}
                onSendMessage={onSendMessage}
                onAfterAction={onAfterAction}
                scrollToRoomOptions={scrollToRoomOptions}
                checkoutSource={checkoutSource}
              />
            </Suspense>
          </>
        )}

        {!isFlashHotel && !isBedbankHotel && hasRoomOptions && (
          <>
            <Divider kind="secondary" />
            <HotelDetailRoomOptions
              roomOptions={roomOptions}
              deeplinkUrl={deeplinkUrl}
            />
          </>
        )}

        {(hasReviews || reviewsLoading) && (
          <>
            <Divider kind="secondary" />
            {reviewsLoading && !hasReviews && (
              <Group direction="vertical" gap={12}>
                <LoadingBox height={20} width={120} borderRadius="S" />
                <LoadingBox height={60} width={320} borderRadius="S" />
              </Group>
            )}
            {hasReviews && (
              <OfferDetailReviews
                rating={rating}
                reviewsTotal={reviewsTotal}
                reviews={reviews}
              />
            )}
          </>
        )}

        {hasGeoLocation && (
          <>
            <Divider kind="secondary" />
            <OfferDetailMap
              geoLocation={geoLocation}
              offerName={name}
            />
          </>
        )}
      </Group>
    </ContentWrapper>
  )
}

export default HotelDetailView
